import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

// ---------Shared--------------------------------------------
import { TylerMatTableDataSource } from '../../shared-components/angular-material/extended.class';
import { detailExpand } from '../../shared-components/angular-material/mat-animations';
import { AriaDirective } from '../../shared-components/directives/aria.directive';
import { Discipline } from '../../shared-components/student/discipline.model';
import { Student } from '../../shared-components/student/student.model';
import { StudentService } from '../../shared-components/student/student.service';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { LoginService } from '../../shared-modules/login/login.service';

// -------Angular Material------------------------------------
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { ReportV9 } from '../../shared-modules/reporting/models/report-v9.model';
import { ReportQueueComponent } from '../../shared-modules/reporting/report-queue/report-queue.component';
import { ReportingService } from '../../shared-modules/reporting/reporting.service';

@AutoUnsubscribe()
@Component({
    selector: 'discipline',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './discipline.component.html',
    styleUrls: ['./discipline.component.css'],
    animations: [detailExpand],
})
export class DisciplineComponent implements OnInit, OnDestroy {

    // ------------Academic History Data---------------------------
    student360YearSubscription: Subscription;
    selectedStudentSubscription: Subscription;
    selectedStudent: Student;
    disciplines: Discipline[] = [];
    // ------------Selection/Flags-----------------------------------
    selected = '0';
    isBusy = false;
    useDisciplineEventModel: boolean;
    parentPortalDisciplineShowComments: boolean;
    isStaffMember = false;
    // ----------Angular Material------------------------------------
    angularMatData = new TylerMatTableDataSource();
    @ViewChild(MatSort) sort: MatSort;
    columnsToDisplay: string[] = ['More', 'DateTime', 'Incident', 'Involvement', 'Action', 'ActionDate', 'Days'];
    initialSelection = [];
    allowMultiSelect = true;
    selection = new SelectionModel<any>(this.allowMultiSelect, this.initialSelection);

    // --------------LifeCycle---------------------------------------

    constructor(private studentService: StudentService,
        private yearService: YearService,
        private userService: UserService,
        private loginService: LoginService,
        private dialog: MatDialog,
        private reportService: ReportingService,
    ) {
        this.selectedStudentSubscription = studentService.selectedStudentStream$.subscribe(
            studentId => {
                this.loadStudentInfo(studentId);
            });

        this.student360YearSubscription = yearService.observableStudent360YearStream$.subscribe(
            studentYear => {
                this.getStudentDiscipline();
            }
        );

        this.angularMatData = new TylerMatTableDataSource(this.disciplines);
        this.isStaffMember = this.userService.userType == 1;
        this.useDisciplineEventModel = this.loginService.LoginSettings.UseDisciplineEventModel;
        this.parentPortalDisciplineShowComments = this.loginService.LoginSettings.ParentPortalDisciplineShowComments;
        // ONE-5840
        // ONE-6659 12/19/19 Comments are NOT to be displayed on Parent & Student Portal.
        if (this.isStaffMember) {
            this.parentPortalDisciplineShowComments = true;
        }
    }

    ngOnInit() {
    }

    ngOnDestroy() {
    }

    // ------------Student Discipline Data---------------------------

    loadStudentInfo(studentId: number) {
        this.selectedStudent = this.studentService.students.filter(function (student) { return student.Id == studentId; })[0];
        this.getStudentDiscipline();
    }

    getStudentDiscipline() {
        this.isBusy = true;

        this.angularMatData.data = [];
        setTimeout(() => {
            this.studentService.getStudentDiscipline(this.selectedStudent.Id, this.yearService.student360Year.Id, this.userService.userId).subscribe(data => {
                this.disciplines = data;
                this.disciplines.forEach(element => {
                    element.BehaviorLetter = element.BehaviorLetterId && element.BehaviorLetterId > 0 ? true : false;
                });
                this.loadStudentDiscipline();
            });
        }, 0);
    }

    private loadStudentDiscipline(): void {

        this.angularMatData.data = this.disciplines;
        setTimeout(() => {
            this.angularMatData.sort = this.sort;
        }, 0);
        this.isBusy = false;
    }
    // ------------Aria/Accessibility---------------------------

    getAriaRow(row: Discipline): string {
        const colList: string[] = ['Date/Time',
            'Incident',
            'Involvement',
            'Action',
            'Action Date',
            'Days'];

        const dataList: string[] = [row.DateTime.toString(),
        row.Incident,
        row.Involvement,
        row.Action,
        ((row.ActionDate.toString() != '0001-01-01T00:00:00') ? new Date(row.ActionDate).toDateString() : ''),
        (row.Days != 0 ? row.Days.toString() : '')];

        return AriaDirective.concatAriaStrings(colList, dataList) + ' Expand for more details';
    }

    getAriaDetailRow(row: Discipline): string {
        const colList: string[] = ['Event',
            'Location',
            'Involving Others',
            'Reported To Police',
            'Reported by'];

        const dataList: string[] = [row.Event,
        row.Location,
        row.InvolvingOthers,
        row.ReportedToPolice,
        row.ReportedBy];

        if (this.parentPortalDisciplineShowComments) {
            if (row.EventComments != null && row.EventComments != '') {
                colList.push('Event Comment');
                dataList.push(row.EventComments);
            }
            if (row.ActionComments != null && row.ActionComments != '') {
                colList.push('Action Comment');
                dataList.push(row.ActionComments);
            }
            if (row.IncidentComments != null && row.IncidentComments != '') {
                colList.push('Incident Comment');
                dataList.push(row.IncidentComments);
            }
        }

        return AriaDirective.concatAriaStrings(colList, dataList);
    }

    // ----------Angular Material------------------------------------
    toggleExpanded(row: any) {
        this.angularMatData.toggleExpanded(row);
    }

    checkExpanded(row: any): boolean {
        return this.angularMatData.checkExpanded(row);
    }

    expandAll(expand: boolean) {
        this.angularMatData.expandAll(expand);
    }

    rowKeyPress(event: KeyboardEvent, row: any) {
        this.angularMatData.rowKeyPress(event, row);
    }

    printReport() {
        this.reportService.getV9Reports('Behavior').subscribe(reports => {
            const behavioReports: ReportV9[] = reports;
            const createProfileReport = behavioReports.find(x => x.ReportName === 'CreateProfileReport');
            // Set Default Values to Parameters
            if (createProfileReport) {
                createProfileReport.StudentIds = this.selectedStudent.Id.toString();
                this.reportService.getV9ReportParameters(createProfileReport.Id, 0, 0).subscribe(parameters => {
                    createProfileReport.ReportParameters = parameters;
                    if (this.yearService.student360Year.StartDate && this.yearService.student360Year.EndDate) {
                        createProfileReport.ReportParameters.find(x => x.ParameterName === 'datIncidentDateFrom').Value = this.yearService.student360Year.StartDate;
                        createProfileReport.ReportParameters.find(x => x.ParameterName === 'datIncidentDateTo').Value = this.yearService.student360Year.EndDate;
                    }
                    else {
                        createProfileReport.ReportParameters.find(x => x.ParameterName === 'blnIncidentDate').Value = false;
                        createProfileReport.ReportParameters.find(x => x.ParameterName === 'strIncidentsIDs').Value = '-1';
                    }

                    this.reportService.runV9ReportByParameter(this.yearService.defaultYear.SchoolID, this.yearService.defaultYear.Id, 0, 0, createProfileReport).subscribe(result => {
                        const reportQueueDialogRef = this.dialog.open(ReportQueueComponent, {
                            panelClass: 'tyl-mat-dialog',
                            disableClose: true,
                        });
                    });
                });
            }
        });
    }
}
